const AGENT_API_BASE = process.env.REACT_APP_AGENT_API_URL || 'http://localhost:8000';
// Default run target when a caller passes no runConfig (see sendMessage). One bundle now serves both
// products via a path-derived runConfig; these env constants remain the fallback for any other caller.
const TEAM_ID = process.env.REACT_APP_TEAM_ID || 'travel_desk_manager';
const AGENT_ID = process.env.REACT_APP_AGENT_ID || '';

// Sentinel strings that some abort paths emit as RunCompleted.content
// (e.g. str(None) → "None"). Treat them as empty so they never render and
// the graceful fallback wins.
const SENTINEL_EMPTY = new Set(['none', 'null', 'undefined']);

// When the agent stops on a tool call WITHOUT generating any spoken text (e.g. it
// runs set_active_company and says nothing), agno backfills RunCompleted /
// TeamRunCompleted .content with a raw tool-execution SUMMARY like
// "set_active_company(company_id=None) completed in 0.0008s." — that is bookkeeping,
// NOT a reply. The ToolCall* lifecycle events are filtered by type below, but these
// run-completed events slip through carrying the summary as content. Match the agno
// summary shape (".. ) completed in <n>s.") and treat it as empty so the raw tool
// line never renders as the assistant message; the graceful fallback wins instead.
const TOOL_SUMMARY_RE = /\)\s+completed in\s+[\d.]+\s*m?s\.?\s*$/;

// When the OpenAI call behind a turn fails (a transient 5xx, rate limit, etc.), agno passes
// the provider's RAW error body through as the run content — e.g. "An error occurred while
// processing your request ... help.openai.com ... Please include the request ID ...". That is
// an internal provider failure, NOT a reply, and must never render to the customer. Treat it
// as empty so the graceful fallback ("I ran into a problem — try again") wins instead.
const PROVIDER_ERROR_RE = /an error occurred while processing your request|help\.openai\.com/i;

// True if `s` carries no real assistant text: empty, a str(None)-style sentinel,
// or a raw tool-execution summary. The sentinel/tool-summary checks used to run
// ONLY on the final RunCompleted event, but agno also streams the str(None) →
// "None" token as RunContent/TeamRunContent DELTAS during a textless
// stop_after_tool_call turn. Those deltas accumulate into the reply buffer and
// rendered as the literal "None". Centralize the check here so it guards the
// accumulated buffer everywhere — deltas, final reconciliation, and reply pick.
const isEmptyish = (s) => {
  const t = (s || '').trim();
  return !t || SENTINEL_EMPTY.has(t.toLowerCase()) || TOOL_SUMMARY_RE.test(t);
};

// Tools that are SAFE to repeat if the turn comes back textless (see the silent-retry
// below). Allowlist, not denylist, so it stays safe-by-default: a NEW or unknown tool is
// never auto-retried. These are read-only lookups plus the two idempotent writes
// (re-running with identical args is a no-op). The booking/quote CREATION tools
// (book_ride, generate_quote, generate_round_trip_quote) are deliberately ABSENT —
// repeating those would double-book or create duplicate draft quotes.
const RETRY_SAFE_TOOLS = new Set([
  // read-only lookups
  'get_vehicle_options', 'resolve_location', 'validate_location',
  'get_customer_profile', 'get_active_companies',
  'get_my_quotes', 'get_my_bookings',
  'get_my_quote_by_multiple_criteria', 'get_my_booking_by_multiple_criteria',
  // idempotent writes (repeating with the same args changes nothing)
  'register_rider_identity', 'set_active_company',
]);

// Maximum re-posts of the same message in one logical turn (shared ceiling for the handoff
// retry and the textless silent-retry), so neither can loop.
const MAX_RETRY_DEPTH = 3;

// Decide whether a TEXTLESS turn (a tool ran but the model printed no text) is SAFE to silently
// retry. Requires: at least one tool ran, EVERY tool that ran is in RETRY_SAFE_TOOLS (so we never
// repeat a booking/quote-creation write), and we're under the recursion bound. Pure + exported so
// the safety rule is unit-tested without standing up the whole SSE stream.
export function isRetrySafeTextless(completedTools, depth) {
  const tools = completedTools instanceof Set ? completedTools : new Set(completedTools || []);
  return tools.size > 0
    && [...tools].every((t) => RETRY_SAFE_TOOLS.has(t))
    && depth < MAX_RETRY_DEPTH;
}

// Tool-call lifecycle event names, split by phase, so we can drive a transient
// per-tool status chip from the stream (the events themselves are never rendered
// as message text — see the tool-event handling below).
const TOOL_START_TYPES = new Set(['ToolCallStarted', 'TeamToolCallStarted']);
const TOOL_END_TYPES = new Set(['ToolCallCompleted', 'ToolCallError', 'TeamToolCallCompleted']);

// In route mode, agno occasionally returns a turn as TWO concatenated generations of the
// same reply (a leader/member double-pass), e.g.
//   "...no flight details are needed.Thanks for the details so far — ...<same question>"
// Both halves restate the same answer; the join is a sentence end immediately followed by an
// uppercase letter (no space), and a long chunk of the reply recurs. When we see that
// signature, keep the first complete reply and drop the duplicate tail. This is a no-op on
// every normal single reply (no long internal repeat). See agents repro: only the doubled
// case has a recurring 40+ char substring.
// A route-form token pinned to the END of the reply. Peoria charter appends `[[ROUTE_FORM:multi={json}]]`
// (the web strips it and renders the map form). Kept in sync with the canonical parser
// ROUTE_FORM_TOKEN_RE in components/ChatMessage.js — same tag set; the seed JSON never contains "]]"
// (the agents side escapes "]"), so the non-greedy body match up to the terminating "]]" is safe.
const TRAILING_ROUTE_FORM_TOKEN_RE =
  /\s*\[\[ROUTE_FORM(?::leg\d+|:two_leg|:return|:multi(?:=[\s\S]*?)?)?\]\]\s*$/i;

// De-double PROSE only. A team turn can come back with the leader's and member's replies concatenated
// (both restate the same answer); keep the first complete reply and drop the duplicate tail. This is a
// no-op on every normal single reply (no long internal repeat). See agents repro: only the doubled case
// has a recurring 40+ char substring.
function collapseDoubledProse(text) {
  const t = (text || '').trim();
  if (t.length < 120) return text;
  // DISPLAY_CARD payloads legitimately repeat field values (a round trip's return destination ==
  // its outbound pickup, so the address appears in both pickupAddress and returnDestinationDetail),
  // which trips the doubled-reply heuristic below and truncates the card JSON — dropping the return*
  // fields and the folded followupPrompt so the card renders with no follow-up bubble. De-doubling is
  // for PROSE leader/member replies only; a machine card payload is never a doubled reply.
  if (t.includes('DISPLAY_CARD:') || t.includes('fullDetails=')) return text;
  const N = 40; // minimum recurring-chunk length that signals a doubled reply
  for (let i = 0; i + N <= t.length - N; i++) {
    const chunk = t.slice(i, i + N);
    const j = t.indexOf(chunk, i + N);
    if (j === -1) continue;
    // Doubled. The seam (reply 1 ends, reply 2 begins) lies between the two occurrences.
    // Prefer the tell-tale no-space ".X" / "?X" / "!X" join; fall back to the last normal
    // sentence break before the repeat.
    const between = t.slice(i + N, j);
    const rel = between.search(/[.?!](?=[A-Z])/);
    let seam;
    if (rel !== -1) {
      seam = i + N + rel + 1;
    } else {
      const k = Math.max(t.lastIndexOf('. ', j), t.lastIndexOf('? ', j), t.lastIndexOf('! ', j));
      seam = k > i ? k + 1 : -1;
    }
    if (seam > 0 && seam < t.length) {
      const first = t.slice(0, seam).trim();
      if (first.length >= N) return first;
    }
    return text;
  }
  return text;
}

export function collapseDoubledReply(text) {
  const raw = text || '';
  // Peel a trailing route-form token BEFORE de-doubling. Its seed JSON repeats the place-object shape
  // (`,"isAirport":false,"iata":null}`, `"state":"IL",…`) for every endpoint, which trips the
  // recurring-substring heuristic and truncates the reply at the PRECEDING prose sentence boundary —
  // silently dropping the token so detectRouteRequest finds nothing and the map form never renders
  // (the reported "route printed as text" bug). De-double the prose alone, then reattach the token
  // verbatim so the web still strips it and opens the form.
  const m = raw.match(TRAILING_ROUTE_FORM_TOKEN_RE);
  if (!m) return collapseDoubledProse(text);   // pass ORIGINAL so null / '' round-trip unchanged
  const token = m[0].trim();
  const prose = raw.slice(0, m.index);
  const collapsed = collapseDoubledProse(prose);
  return `${(collapsed || '').replace(/\s+$/, '')} ${token}`.trim();
}

// Content streaming events. agno emits RunContent/TeamRunContent as token DELTAS
// (each carries only the newly-generated text), and RunCompleted/TeamRunCompleted
// once with the FULL final text. We accumulate deltas to render progressively, then
// let the completed event reconcile to the clean authoritative text.
const CONTENT_DELTA_TYPES = new Set(['RunContent', 'TeamRunContent']);
const CONTENT_FINAL_TYPES = new Set(['RunCompleted', 'TeamRunCompleted']);

// ── Route-once routing ────────────────────────────────────────────────────────
// The first message of a session is classified to an OWNER agent via POST /agents/route; every
// later turn goes straight to /agents/{owner}/runs (no per-turn team delegation). An agent can hand
// off by replying exactly "HANDOFF::<agent_id>", which switches the owner and re-posts the message.
// If classification ever fails we fall back to the team route, so chat keeps working either way.
const sessionOwners = new Map(); // sessionId -> owner agent id
const HANDOFF_RE = /^HANDOFF::([a-z_]+)\s*$/i;
export const isHandoff = (s) => HANDOFF_RE.test((s || '').trim());
export const handoffTarget = (s) => { const m = HANDOFF_RE.exec((s || '').trim()); return m ? m[1] : null; };

export async function classifyOwner(message, token) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const r = await fetch(`${AGENT_API_BASE}/route`, {
      method: 'POST', headers, body: JSON.stringify({ message }),
    });
    if (!r.ok) return null;
    const data = await r.json();
    return data && data.owner ? data.owner : null;
  } catch (_) {
    return null; // degrade to the team route
  }
}

export async function sendMessage(message, sessionId, userId, token, onChunk, onDone, onError, onStatus, depth = 0, runConfig = null) {
  // The run target is per-product and comes from the path (resolveProduct) so ONE bundle serves both
  // /r3chat (team `travel_desk_manager`, route-once) and /peoriachat (agent `charter_quote_agent`,
  // direct). Falls back to the build-time env constants when no runConfig is passed (other callers).
  const teamId = runConfig ? (runConfig.teamId || TEAM_ID) : TEAM_ID;
  const agentId = runConfig ? (runConfig.agentId || '') : AGENT_ID;
  // Optional status callback: receives a tool_name string while a tool is
  // in-flight, or null when nothing is running. Default to a no-op so existing
  // callers keep working.
  const emitStatus = typeof onStatus === 'function' ? onStatus : () => {};
  // Track in-flight tools by tool_call_id so batched/parallel tool calls (the
  // agent can emit validate_location + get_vehicle_options back-to-back) don't
  // clear the chip prematurely. Status follows the most-recently-started tool
  // still running; clears only when all have completed.
  const inFlight = new Map(); // tool_call_id -> tool_name
  const currentStatus = () => {
    const names = Array.from(inFlight.values());
    return names.length ? names[names.length - 1] : null;
  };

  const formData = new FormData();
  formData.append('message', message);
  formData.append('stream', 'true');
  if (sessionId) formData.append('session_id', sessionId);
  if (userId) formData.append('user_id', userId);

  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    // Charter (agentId set) posts DIRECTLY to that single agent — there's one agent, so no route-once
    // classification. Rides (no agentId) use route-once: reuse this session's owner, or classify the
    // first message; fall back to the team route if classification fails.
    let owner = null;
    let runUrl;
    if (agentId) {
      runUrl = `${AGENT_API_BASE}/agents/${agentId}/runs`;
    } else {
      owner = (sessionId && sessionOwners.get(sessionId)) || null;
      if (!owner) owner = await classifyOwner(message, token);
      // AgentOS serves each agent at {AGENT_API_BASE}/agents/{agent_id}/runs (AGENT_API_BASE already
      // ends in /agents, so this is the double-/agents path agno mounts); ids are used verbatim.
      runUrl = owner
        ? `${AGENT_API_BASE}/agents/${owner}/runs`
        : `${AGENT_API_BASE}/teams/${teamId}/runs`;
    }

    const response = await fetch(runUrl, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errText = await response.text();
      onError(`API error: ${response.status} ${errText}`);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let sessionIdFromResponse = sessionId;
    // Route mode streams BOTH the team-leader's content (Team-prefixed events) and
    // the delegated member agent's content. The leader sometimes generates its own
    // answer IN ADDITION to delegating, which doubles the reply. Track them apart
    // and prefer the member's; fall back to the leader's only when no member
    // content arrived (e.g. a guardrail block, which is leader-only).
    let memberContent = '';
    let teamContent = '';
    // Did a tool actually run-and-complete this turn? Used to distinguish a benign
    // "tool ran but the model emitted no text" turn (e.g. a silent gate tool like
    // set_active_company / register_rider_identity) from a genuine empty/errored turn.
    // A successful, textless tool turn must NOT surface as a self-blaming error.
    let toolCompletedOk = false;
    // Names of tools that completed OK this turn — so a textless turn can decide whether it's
    // SAFE to silently retry (only when every tool that ran is in RETRY_SAFE_TOOLS).
    const completedTools = new Set();
    // Prefer the member's reply, but only if it carries real text. A textless
    // stop_after_tool_call turn leaves "None" in one buffer; fall through to the
    // other rather than letting the sentinel shadow a genuine reply. Returns ''
    // when neither buffer has real content.
    const pickReply = () =>
      (!isEmptyish(memberContent) && memberContent)
      || (!isEmptyish(teamContent) && teamContent)
      || '';
    // Never render a HANDOFF:: marker mid-stream — it's a routing signal, acted on after the stream.
    const emitReply = (c, sid) => { if (!isHandoff(c)) onChunk(c, sid); };

    while (true) {
      const { done, value } = await reader.read();

      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });

      // Split on the SSE event delimiter. Mid-stream, hold the trailing partial event back for the
      // next read. On stream END, process EVERYTHING still buffered instead of dropping it: a large
      // final event — the full RunCompleted card — can arrive without a trailing "\n\n", and dropping
      // it truncated the card's closing brace, so the client fell back to rendering raw JSON text.
      const events = buffer.split('\n\n');
      buffer = done ? '' : (events.pop() || '');

      for (const event of events) {
        const lines = event.split('\n');
        let eventType = '';
        let dataStr = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataStr += line.slice(5).trim();
          }
        }

        if (!dataStr) continue;

        try {
          const data = JSON.parse(dataStr);

          if (data.session_id) {
            sessionIdFromResponse = data.session_id;
          }

          // Drive the transient status chip from tool-call lifecycle events,
          // then skip them as message text. Their `content` is a raw tool-call
          // summary, not a reply, so it must never become the assistant message.
          const toolName = data.tool?.tool_name || data.tool_name || null;
          const toolCallId = data.tool?.tool_call_id || data.tool_call_id || toolName;
          if (TOOL_START_TYPES.has(eventType)) {
            if (toolName) inFlight.set(toolCallId, toolName);
            emitStatus(currentStatus());
            continue;
          }
          if (TOOL_END_TYPES.has(eventType)) {
            inFlight.delete(toolCallId);
            // A successful completion (not ToolCallError) means a tool genuinely ran
            // this turn — remember it (and its name) so a textless turn degrades gracefully below.
            if (eventType !== 'ToolCallError') {
              toolCompletedOk = true;
              if (toolName) completedTools.add(toolName);
            }
            emitStatus(currentStatus());
            continue;
          }
          // Defense-in-depth: skip any other event that carries tool execution
          // metadata (no status change — these are bookkeeping, not lifecycle).
          if (Array.isArray(data.tool_executions) && data.tool_executions.length > 0) {
            continue;
          }

          const content = data.content
            || data.response?.content
            || data.text
            || data.message;

          if (content && typeof content === 'string') {
            const isTeam = eventType.startsWith('Team');
            if (CONTENT_DELTA_TYPES.has(eventType)) {
              // Token delta: append so the message types out live. We don't run the full
              // sentinel/tool-summary filters per token (those match a whole reply), EXCEPT:
              // drop a sentinel ("None"/"null"/...) when it's the SOLE content so far — that's
              // a stray str(None) abort arriving as a one-token reply, not real text. A sentinel
              // token mid-reply (buffer already non-empty) is left untouched.
              const buf = isTeam ? teamContent : memberContent;
              if (buf === '' && SENTINEL_EMPTY.has(content.trim().toLowerCase())) {
                continue;
              }
              if (isTeam) {
                teamContent += content;
              } else {
                memberContent += content;
              }
              // Render progressively — CustomerApp's onChunk replaces the in-progress
              // assistant message with this running text. Prefer the member's stream,
              // and never flash a pure sentinel ("None") while the turn is textless.
              emitReply(pickReply(), sessionIdFromResponse);
            } else {
              // Final (RunCompleted/TeamRunCompleted) or any other content-bearing
              // event: the content is the FULL authoritative reply. Filter sentinels /
              // raw tool summaries, then REPLACE the accumulated buffer (idempotent with
              // the deltas) so the clean final text wins.
              const trimmed = content.trim();
              if (trimmed && !SENTINEL_EMPTY.has(trimmed.toLowerCase())
                  && !TOOL_SUMMARY_RE.test(trimmed)
                  && !PROVIDER_ERROR_RE.test(trimmed)) {
                if (isTeam) {
                  teamContent = content;
                } else {
                  memberContent = content;
                }
                if (CONTENT_FINAL_TYPES.has(eventType)) {
                  emitReply(pickReply(), sessionIdFromResponse);
                }
              }
            }
          }
        } catch (e) {
          // non-JSON data, ignore
        }
      }

      if (done) break;
    }

    // Stream ended: clear any lingering status so the chip gives way to the
    // final assistant text (the menu / answer / question).
    emitStatus(null);

    // Prefer the member agent's answer; fall back to the leader's content only
    // when the member produced none (guardrail block / leader-only reply). This
    // drops the router's duplicate "answer" without hiding genuine team replies.
    // pickReply() also drops a sentinel "None"/tool-summary buffer so a textless
    // stop_after_tool_call turn degrades to the graceful fallback below instead of
    // rendering the literal "None". collapseDoubledReply additionally guards the
    // rarer case where a SINGLE content string contains the reply twice.
    let lastContent = collapseDoubledReply(pickReply());
    // If a provider error leaked through as the streamed reply, blank it so the graceful
    // fallback below fires instead of showing the customer a raw "request ID" error.
    if (lastContent && PROVIDER_ERROR_RE.test(lastContent)) {
      lastContent = '';
    }

    // Route-once handoff: the agent asked to switch owners. Update the owner and re-post the SAME
    // message to the new owner (bounded depth), without rendering the marker. The new owner resumes
    // the session, so nothing is re-collected. (Charter never emits a handoff — single agent.)
    if (isHandoff(lastContent)) {
      const target = handoffTarget(lastContent);
      const sid = sessionIdFromResponse;
      if (target && sid) sessionOwners.set(sid, target);
      emitStatus(null);
      if (target && depth < MAX_RETRY_DEPTH) {
        return sendMessage(message, sid, userId, token, onChunk, onDone, onError, onStatus, depth + 1, runConfig);
      }
      onChunk('Got that — what would you like to do next?', sid);
      onDone(sid);
      return;
    }

    // Remember this session's owner so later turns skip classification (rides only; null for charter).
    if (owner && sessionIdFromResponse) sessionOwners.set(sessionIdFromResponse, owner);

    // Only send the final content to the UI
    if (lastContent) {
      onChunk(lastContent, sessionIdFromResponse);
    } else if (toolCompletedOk) {
      // A tool ran and completed fine, but the model emitted no user-facing text — an
      // intermittent textless turn (e.g. it ran get_vehicle_options after a bag correction
      // and forgot to print the menu, so the booking stalls). SILENT RETRY: if every tool
      // that ran is safe to repeat (RETRY_SAFE_TOOLS), re-post the SAME message once. Since
      // the glitch is random, the retry almost always returns the real text, so the rider
      // never sees the hiccup. Bounded by `depth` (shared with the handoff retry) so it can
      // never loop. We do NOT retry when a booking/quote-CREATION tool ran (absent from the
      // allowlist) — repeating that could double-book; we fall straight to the nudge instead.
      if (isRetrySafeTextless(completedTools, depth)) {
        emitStatus(null);
        return sendMessage(
          message, sessionIdFromResponse, userId, token,
          onChunk, onDone, onError, onStatus, depth + 1, runConfig,
        );
      }
      // Retry not safe (a write tool ran) or exhausted — do NOT show the self-blaming
      // "could you try rephrasing?" (nothing broke); show a gentle, neutral nudge.
      onChunk(
        "Got that — what would you like to do next?",
        sessionIdFromResponse
      );
    } else {
      // No tool ran and no text was produced (genuine errored/aborted turn). Never
      // leave a raw tool/delegation event as the reply — show a graceful fallback.
      onChunk(
        "Sorry, I ran into a problem with that — could you try rephrasing?",
        sessionIdFromResponse
      );
    }

    onDone(sessionIdFromResponse);
  } catch (err) {
    emitStatus(null);
    onError(err.message || 'Network error');
  }
}
